/**
 * Recurrence occurrence generation, shared by every calendar create form.
 *
 * Previously this logic was copy-pasted (and had drifted) between the main
 * calendar form and the no-FullCalendar fallback form. Centralizing it keeps a
 * single source of truth for: rrule vs. hand-rolled fallback, the end-date
 * boundary, the infinite-recurrence guard, and holiday-week skipping.
 *
 * All Dates are treated as local wall-clock; callers format them with
 * toLocalDateTimeString() before sending to the API.
 */
import { isInHolidayWeek } from './holidays'

export type RepeatFreq = 'DAILY' | 'WEEKLY' | 'MONTHLY'
export type RepeatEndMode = 'until' | 'count'
export type MonthlyMode = 'DAY_OF_MONTH' | 'NTH_WEEKDAY'

export interface RecurrenceOptions {
  repeatEnabled: boolean
  repeatFreq: RepeatFreq
  repeatInterval: number
  repeatEndMode: RepeatEndMode
  repeatUntil: string // YYYY-MM-DD
  repeatCount: number | ''
  repeatWeekdays: Set<number> // 0=Sun .. 6=Sat
  monthlyMode: MonthlyMode
  /** For MONTHLY + NTH_WEEKDAY: 1..4 = first..fourth, -1 = last. */
  monthlyOrdinal: number
  skipHolidays: boolean
  holidays: Array<{ date: Date; name: string }>
  /** The imported `rrule` module (or null/undefined if it failed to load). */
  rrule?: any
}

const DAY_MS = 24 * 60 * 60 * 1000
const DEFAULT_MAX_OCCURRENCES = 52 // safety cap when no end date / count is given

/** Parse the user's end date at end-of-local-day so an occurrence ON that day is kept. */
function parseUntil(repeatUntil: string): Date | null {
  return repeatUntil ? new Date(`${repeatUntil}T23:59:59`) : null
}

/**
 * rrule.js interprets dtstart/until/byweekday using the Date's UTC fields, not
 * local ones. Feeding a local Date breaks weekday math whenever local->UTC
 * crosses midnight (e.g. an evening start in winter), shifting occurrences to
 * the wrong day. The documented workaround: pass a UTC date whose UTC fields
 * equal the desired LOCAL wall-clock, then read results back the same way.
 */
function toRRuleDate(d: Date): Date {
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()))
}
function fromRRuleDate(d: Date): Date {
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds())
}

/**
 * Build the full list of occurrence Dates (local), always including `start`.
 * Holiday-week skipping is applied uniformly at the end; the chosen start is
 * always kept even if it lands in a holiday week.
 */
export function generateOccurrences(start: Date, opts: RecurrenceOptions): Date[] {
  const occurrences: Date[] = [new Date(start)]

  if (opts.repeatEnabled) {
    const RRule = opts.rrule?.RRule
    if (RRule) {
      occurrences.push(...generateWithRRule(start, opts, RRule))
    } else {
      occurrences.push(...generateFallback(start, opts))
    }
  }

  if (opts.repeatEnabled && opts.skipHolidays) {
    return occurrences.filter((d, idx) => idx === 0 || !isInHolidayWeek(d, opts.holidays).isHoliday)
  }
  return occurrences
}

function generateWithRRule(start: Date, opts: RecurrenceOptions, RRule: any): Date[] {
  const ro: any = {
    freq: RRule[opts.repeatFreq],
    interval: Number(opts.repeatInterval) || 1,
    dtstart: toRRuleDate(start),
  }
  const until = opts.repeatEndMode === 'until' ? parseUntil(opts.repeatUntil) : null
  if (until) ro.until = toRRuleDate(until)
  if (opts.repeatEndMode === 'count' && opts.repeatCount) ro.count = Number(opts.repeatCount)

  const wd = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA]
  if (opts.repeatFreq === 'WEEKLY' && opts.repeatWeekdays.size > 0) {
    ro.byweekday = Array.from(opts.repeatWeekdays).map((d) => wd[d])
  }
  if (opts.repeatFreq === 'MONTHLY') {
    if (opts.monthlyMode === 'DAY_OF_MONTH') {
      ro.bymonthday = [start.getDate()]
    } else {
      ro.byweekday = [wd[start.getDay()]]
      ro.bysetpos = [opts.monthlyOrdinal] // 1..4 or -1 (last)
    }
  }
  // Without an end date or count, rule.all() would be infinite.
  if (!ro.until && !ro.count) ro.count = DEFAULT_MAX_OCCURRENCES

  const rule = new RRule(ro)
  const startTime = start.getTime()
  // Map rrule's UTC-encoded results back to local Dates, dropping the start
  // (we always include it explicitly in generateOccurrences).
  return rule.all().map(fromRRuleDate).filter((d: Date) => d.getTime() !== startTime)
}

/** Hand-rolled fallback used only when the rrule library fails to load. */
function generateFallback(start: Date, opts: RecurrenceOptions): Date[] {
  const out: Date[] = []
  const interval = Number(opts.repeatInterval) || 1
  const end = opts.repeatEndMode === 'until' ? parseUntil(opts.repeatUntil) : null
  // maxCount counts total occurrences including the start.
  const maxCount = opts.repeatEndMode === 'count' && opts.repeatCount ? Number(opts.repeatCount) : null
  const hardLimit = new Date(start.getTime() + 366 * DAY_MS)
  const reachedLimit = () => {
    if (maxCount) return out.length + 1 >= maxCount
    if (!end) return out.length + 1 >= DEFAULT_MAX_OCCURRENCES
    return false
  }

  if (opts.repeatFreq === 'DAILY') {
    const cursor = new Date(start)
    while (!reachedLimit()) {
      cursor.setDate(cursor.getDate() + interval)
      if (end && cursor > end) break
      if (cursor > hardLimit) break
      out.push(new Date(cursor))
    }
  } else if (opts.repeatFreq === 'WEEKLY') {
    const days = opts.repeatWeekdays.size > 0 ? Array.from(opts.repeatWeekdays) : [start.getDay()]
    const cursor = new Date(start)
    while (!reachedLimit()) {
      cursor.setDate(cursor.getDate() + 1)
      if (end && cursor > end) break
      if (cursor > hardLimit) break
      if (days.includes(cursor.getDay())) out.push(new Date(cursor))
    }
  } else if (opts.repeatFreq === 'MONTHLY') {
    const cursor = new Date(start)
    while (!reachedLimit()) {
      if (opts.monthlyMode === 'DAY_OF_MONTH') {
        cursor.setMonth(cursor.getMonth() + interval)
      } else {
        const base = new Date(cursor)
        base.setMonth(base.getMonth() + interval)
        const targetMonth = base.getMonth()
        const targetYear = base.getFullYear()
        const startWeekday = start.getDay()
        const firstW = new Date(targetYear, targetMonth, 1).getDay()
        const daysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate()
        const firstMatch = 1 + ((7 + startWeekday - firstW) % 7) // first <weekday> of month
        let day: number
        if (opts.monthlyOrdinal === -1) {
          // last <weekday> of the month
          day = firstMatch + Math.floor((daysInMonth - firstMatch) / 7) * 7
        } else {
          day = firstMatch + (opts.monthlyOrdinal - 1) * 7
          if (day > daysInMonth) day -= 7
        }
        base.setDate(day)
        cursor.setTime(base.getTime())
      }
      if (end && cursor > end) break
      if (cursor > hardLimit) break
      out.push(new Date(cursor))
    }
  }
  return out
}
